/**
 * Inventory Query Keys
 *
 * Centralized query key factory for all inventory-related queries.
 * Supports matter-level and institution-filtered inventory items.
 */

import type { InventoryFilters } from './types';

/**
 * Inventory query key factory
 *
 * @example
 * ```typescript
 * // Invalidate all inventory for a matter
 * queryClient.invalidateQueries({ queryKey: inventoryKeys.forMatter(matterId) })
 *
 * // Get institution-filtered inventory
 * useQuery({
 *   queryKey: inventoryKeys.list(matterId, { institutionId: 1 }),
 *   queryFn: ...
 * })
 * ```
 */
export const inventoryKeys = {
  /**
   * Root key for all inventory queries
   * Note: Uses 'inventory' to match existing implementation
   */
  all: ['inventory'] as const,

  /**
   * Base key for inventory lists
   */
  lists: () => [...inventoryKeys.all, 'list'] as const,

  /**
   * Inventory items for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) =>
    [...inventoryKeys.lists(), matterId] as const,

  /**
   * Filtered inventory list
   * @param matterId - The matter ID
   * @param filters - Optional filters (institutionId, status, etc.)
   */
  list: (matterId: number, filters?: InventoryFilters) =>
    filters
      ? ([
          ...inventoryKeys.forMatter(matterId),
          JSON.stringify(filters)
        ] as const)
      : inventoryKeys.forMatter(matterId),

  /**
   * Base key for inventory item details
   */
  items: () => [...inventoryKeys.all, 'item'] as const,

  /**
   * Specific inventory item
   * @param matterId - The matter ID
   * @param itemId - The inventory item ID
   */
  detail: (matterId: number, itemId: number) =>
    [...inventoryKeys.items(), matterId, itemId] as const,

  /**
   * Value-confirmation source options for an item (institution-response
   * attachments the manual picker can cite — LF-5393).
   * @param matterId - The matter ID
   * @param itemId - The inventory item ID
   */
  valueConfirmationSources: (matterId: number, itemId: number) =>
    [
      ...inventoryKeys.detail(matterId, itemId),
      'value-confirmation-sources'
    ] as const,

  /**
   * Inventory grouped by category
   * @param matterId - The matter ID
   */
  grouped: (matterId: number) =>
    [...inventoryKeys.forMatter(matterId), 'grouped'] as const,

  /**
   * Inventory summary (totals, counts)
   * @param matterId - The matter ID
   */
  summary: (matterId: number) =>
    [...inventoryKeys.forMatter(matterId), 'summary'] as const,

  /**
   * Current View BFF (Outstanding / Distributed two-section view-model).
   * Sits under `forMatter` so any inventory-list invalidation cascades to it.
   * @param matterId - The matter ID
   */
  currentView: (matterId: number) =>
    [...inventoryKeys.forMatter(matterId), 'current-view'] as const,

  /**
   * Receipts of money into the estate for a matter.
   *
   * Sits under `forMatter` so recording a realisation — which writes a receipt
   * at source — cascades to the receipts list without a bespoke invalidation.
   * @param matterId - The matter ID
   */
  receipts: (matterId: number) =>
    [...inventoryKeys.forMatter(matterId), 'receipts'] as const,

  /**
   * Inventory types (reference data)
   */
  types: () => ['inventory-types'] as const,

  /**
   * Specific inventory type
   * @param slug - The type slug
   */
  type: (slug: string) => ['inventory-type', slug] as const,

  /**
   * Inventory type metadata
   * @param slug - The type slug
   */
  typeMetadata: (slug: string) =>
    [...inventoryKeys.type(slug), 'metadata'] as const
} as const;
